import { useEffect, useRef, useState } from 'react'

export const getYoutubeThumbnail = url => {
  const regex = new RegExp(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*?[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
    'gi'
  )
  const videoId =
    url.match(regex)?.[0].split('/')[1].replace('watch?v=', '') || null
  return videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : null
}

export const getVimeoThumbnail = async url => {
  const regex = /vimeo\.com\/(?:.*\/)?(\d+)/
  const match = url.match(regex)
  if (!match) return null
  const videoId = match[1]

  try {
    const response = await fetch(
      `https://vimeo.com/api/v2/video/${videoId}.json`
    )
    const data = await response.json()
    return data[0].thumbnail_medium
  } catch (error) {
    console.error('Error fetching Vimeo thumbnail:', error)
    return null
  }
}

export const getMatterportThumbnail = url => {
  const regex = /(?:\/space\/|[?&]m=)([a-zA-Z0-9]+)/
  const match = url.match(regex)
  const modelId = match ? match[1] : null
  return modelId
    ? `https://my.matterport.com/api/v1/player/models/${modelId}/thumb`
    : null
}

export const getMetarealThumbnail = url => {
  const regex = /[?&]asset=([^&]+)/
  const match = url.match(regex)
  const tourId = match ? match[1] : null
  return tourId
    ? `https://tour.metareal.com/view/asset/${tourId}/res/loading-screen.jpeg`
    : null
}

// extract thumbnail from video url
export const useVideoThumbnail = videoData => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const thumbnailUrlRef = useRef(null)

  useEffect(() => {
    if (!videoData) {
      return
    }

    const fetchThumbnail = async () => {
      let thumbnailUrl = null

      if (videoData.link_service === 'uploaded') {
        if (videoData.link_url) {
          const video = document.createElement('video')
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          videoRef.current = video
          canvasRef.current = canvas

          video.crossOrigin = 'anonymous'
          video.muted = true
          video.playsInline = true

          video.addEventListener('loadeddata', () => {
            video.currentTime = 0.1
          })

          video.addEventListener('seeked', () => {
            try {
              if (video.videoWidth === 0 || video.videoHeight === 0) {
                console.log(
                  'Video dimensions not ready, skipping frame extraction'
                )
                return
              }

              canvas.width = video.videoWidth
              canvas.height = video.videoHeight

              ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

              try {
                const dataURL = canvas.toDataURL('image/jpeg', 0.5)
                setThumbnailUrl(dataURL)
                thumbnailUrlRef.current = dataURL
              } catch (blobErr) {
                console.error('Error creating data URL:', blobErr)
              }
            } catch (err) {
              console.error('Error extracting video frame:', err)
            }
          })

          video.src = videoData.link_url
          video.load()
        }
      } else if (videoData.link_service === 'youtube') {
        thumbnailUrl = getYoutubeThumbnail(videoData.link_url)
        setThumbnailUrl(thumbnailUrl)
        thumbnailUrlRef.current = thumbnailUrl
      } else if (videoData.link_service === 'vimeo') {
        thumbnailUrl = await getVimeoThumbnail(videoData.link_url)
        setThumbnailUrl(thumbnailUrl)
        thumbnailUrlRef.current = thumbnailUrl
      } else if (videoData.link_service === 'matterport') {
        thumbnailUrl = getMatterportThumbnail(videoData.link_url)
        setThumbnailUrl(thumbnailUrl)
        thumbnailUrlRef.current = thumbnailUrl
      } else if (videoData.link_service === 'metareal') {
        thumbnailUrl = getMetarealThumbnail(videoData.link_url)
        setThumbnailUrl(thumbnailUrl)
        thumbnailUrlRef.current = thumbnailUrl
      }
      // Note: Unable to find any public APIs for fetching
      // thumbnails for eyexpo and iguide 3d tours
    }

    fetchThumbnail()

    return () => {
      if (thumbnailUrlRef.current) {
        URL.revokeObjectURL(thumbnailUrlRef.current)
      }

      if (videoRef.current) {
        videoRef.current.src = ''
        videoRef.current = null
      }

      canvasRef.current = null
    }
  }, [videoData])

  return thumbnailUrl
}
